<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { minLength, required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import languageOptions from '../supportedLanguages.json';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import FileUpload from 'vue-upload-component';
import { MAXIMUM_FILE_UPLOAD_SIZE } from 'shared/constants/messages';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  selectedTemplate: {
    type: Object,
    default: () => ({}),
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
  agentBot: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const templateId = ref('');
const name = ref('');
const language = ref('');
const category = ref('');
const message = ref('');
const headerType = ref('NONE');
const mediaHeaderType = ref('');
const attachedFiles = ref([]);
const processedParams = ref({});
const footerText = ref('');
const uploadRef = ref(null);

const categories = ['MARKETING', 'UTILITY', 'AUTHENTICATION'].map(item => ({
  value: item,
  label: item.charAt(0).toUpperCase() + item.slice(1).toLowerCase(),
}));
// Full: ['NONE', 'TEXT', 'MEDIA']
const headerTypes = ['NONE', 'MEDIA'].map(item => ({
  value: item,
  label: t(`WHATSAPP_TEMPLATES_MGMT.FORM.COMPONENTS.HEADER.OPTIONS.${item}`),
}));
// Full: ['IMAGE', 'VIDEO', 'DOCUMENT', 'LOCATION']
const mediaHeaderTypes = ['IMAGE', 'VIDEO', 'DOCUMENT'].map(item => ({
  value: item,
  label: item.charAt(0).toUpperCase() + item.slice(1).toLowerCase(),
}));

const addTemplate = reactive({
  showLoading: false,
  message: '',
});

const rules = computed(() => ({
  name: { required, minLength: minLength(2) },
  language: { required },
  category: { required },
  message: { required },
}));

const languageLabel = computed(
  () => languageOptions.find(item => item.value === language.value)?.label ?? ''
);

const v$ = useVuelidate(rules, { name, language, category, message });

const resetForm = () => {
  name.value = '';
  language.value = '';
  category.value = '';
  message.value = '';
  v$.value.$reset();
};

const resetMediaFile = () => {
  attachedFiles.value = [];
};

const processVariable = str => {
  return str.replace(/{{|}}/g, '');
};

const parseMessageFromBot = () => {
  let botSystemPrompt = '';
  if (props.agentBot && props.agentBot.bot_config?.system_prompt)
    botSystemPrompt = JSON.parse(props.agentBot.bot_config.system_prompt);
  let variableDict =
    botSystemPrompt && botSystemPrompt[props.selectedTemplate.name]
      ? botSystemPrompt[props.selectedTemplate.name]
      : {};

  let messageTmp = props.selectedTemplate.components.find(
    component => component.type === 'BODY'
  )?.text;
  let messageLines = messageTmp.split('\n');
  let processedLines = [];
  for (let i = 0; i < messageLines.length; i += 1) {
    if (messageLines[i].trim() === '') processedLines.push(messageLines[i]);
    else {
      let tmpString = messageLines[i].replace(
        /{{([^}]+)}}/g,
        (match, variable) => {
          const variableKey = processVariable(variable);
          return variableDict[variableKey] || `{{${variable}}}`;
        }
      );
      processedLines.push(tmpString);
    }
  }
  return processedLines.join('\n');
};

const variables = () => {
  const variablesTmp = message.value
    .match(/{{([^}]+)}}/g)
    ?.map(variable => processVariable(variable));
  variablesTmp?.forEach(variable => {
    if (!(variable in processedParams.value)) {
      processedParams.value[variable] = '';
    }
  });
  Object.keys(processedParams.value).forEach(variable => {
    if (!variablesTmp?.includes(variable))
      delete processedParams.value[variable];
  });
  return variablesTmp;
};

const populateEditForm = () => {
  templateId.value = props.selectedTemplate.id || '';
  name.value = props.selectedTemplate.name || '';
  category.value = props.selectedTemplate.category || '';
  language.value = props.selectedTemplate.language || '';

  let headerTemplate = props.selectedTemplate.components.find(
    component =>
      component.type === 'HEADER' &&
      ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(component.format)
  );
  if (headerTemplate) {
    headerType.value = 'MEDIA';
    mediaHeaderType.value = mediaHeaderTypes.filter(
      item => item.value === headerTemplate.format.toLocaleString()
    )[0].value;
    let file_url = headerTemplate.example.header_handle[0];
    attachedFiles.value = [
      {
        id: 1,
        content_type: `${headerTemplate.format.toLowerCase()}/${file_url.split('/').pop().split('.').pop()}`,
        file_url: file_url,
        filename: file_url.split('/').pop(),
        byte_size: 1,
      },
    ];
  }

  message.value = parseMessageFromBot();

  let footerTemplate = props.selectedTemplate.components.find(
    component => component.type === 'FOOTER'
  );
  if (footerTemplate) footerText.value = footerTemplate.text;
  variables();
};

onMounted(() => {
  if (props.mode === 'edit') {
    populateEditForm();
  }
});

const getTranslationKey = base => {
  return props.mode === 'edit'
    ? `WHATSAPP_TEMPLATES_MGMT.EDIT.${base}`
    : `WHATSAPP_TEMPLATES_MGMT.ADD.${base}`;
};

const modalTitle = computed(() => t(getTranslationKey('TITLE')));
const modalDescription = computed(() => t(getTranslationKey('DESC')));
const submitButtonText = computed(() => t(getTranslationKey('BUTTON_TEXT')));
const processMessageByRules = () => {
  let messageLines = message.value.split('\n');
  let cachedVariables = {
    variable: {},
  };

  let processedLines = [];
  let paramIndex = 1;
  for (let i = 0; i < messageLines.length; i += 1) {
    if (messageLines[i].trim() === '') processedLines.push(messageLines[i]);
    else {
      processedLines.push(
        messageLines[i].replace(
          messageLines[i].trim(),
          `{{${paramIndex}}}${' ­'.repeat(Math.floor(Math.random() * 7 + 3))}`
        )
      ); // contains invisible character in GUI if create new variable
      cachedVariables[`${paramIndex}`] = messageLines[i].trim();
      paramIndex += 1;
    }
  }
  Object.keys(processedParams.value).forEach(key => {
    cachedVariables.variable['' + key] = processedParams.value[key];
  });

  return {
    processedMessage: processedLines.join('\n'),
    cachedVariables,
  };
};

const handleTemplate = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  addTemplate.showLoading = true;
  try {
    let templateDetails = new FormData();
    if (props.mode === 'edit')
      templateDetails.append('templateId', templateId.value);
    templateDetails.append('name', name.value);
    templateDetails.append('category', category.value);
    templateDetails.append('language', language.value);

    let components = [];
    if (headerType.value === 'MEDIA' && attachedFiles.value.length === 1) {
      components.push({
        type: 'HEADER',
        format: mediaHeaderType.value,
        example: {
          header_handle: [],
        },
      });
    }
    if (message.value !== '') {
      let { processedMessage, cachedVariables } = processMessageByRules();
      let bodyComponent = {
        text: processedMessage,
        type: 'BODY',
        example: {
          body_text: [
            Array(Object.keys(cachedVariables).length - 1).fill(
              'Please confirm the above information that you purchased'
            ),
          ], // Change text for bypass Whatsapp's bot
        },
      };
      components.push(bodyComponent);
      templateDetails.append(
        'cachedVariables',
        JSON.stringify(cachedVariables)
      );
    }
    if (footerText.value !== '') {
      components.push({
        type: 'FOOTER',
        text: footerText.value,
      });
    }
    templateDetails.append('components', JSON.stringify(components));
    attachedFiles.value.forEach(file => {
      if (file.resource)
        templateDetails.append('attachments[]', file.resource.file);
      else
        templateDetails.append(
          'attachments[]',
          JSON.stringify({ file_url: file.file_url })
        );
    });

    if (props.mode === 'edit') {
      console.log(templateDetails);
      await store.dispatch('inboxes/editTemplate', {
        inboxId: props.inboxId,
        templateDetails,
      });
      useAlert(t('WHATSAPP_TEMPLATES_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('inboxes/createTemplate', {
        inboxId: props.inboxId,
        templateDetails,
      });
      useAlert(t('WHATSAPP_TEMPLATES_MGMT.ADD.API.SUCCESS_MESSAGE'));
    }

    resetForm();
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.message || t(`WHATSAPP_TEMPLATES_MGMT.FORM.API.ERROR_MESSAGE`);
    useAlert(errorMessage);
  } finally {
    addTemplate.showLoading = false;
  }
};

const isSubmitDisabled = computed(
  () => v$.value.$invalid || addTemplate.showLoading
);

const attachFile = ({ blob, file }) => {
  const reader = new FileReader();
  reader.readAsDataURL(file.file);
  reader.onloadend = () => {
    attachedFiles.value = [
      {
        resource: blob || file,
        thumb: reader.result,
        blobSignedId: blob ? blob.signed_id : undefined,
      },
    ];
  };
};

const onIndirectFileUpload = file => {
  const MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE = MAXIMUM_FILE_UPLOAD_SIZE;
  if (!file) {
    return;
  }
  if (checkFileSizeLimit(file, MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE)) {
    attachFile({ file });
  } else {
    useAlert(
      t('CONVERSATION.FILE_SIZE_LIMIT', {
        MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE,
      })
    );
  }
};

const resetAttachment = () => {
  attachedFiles.value = [];
};

const handleNameChange = () => {
  name.value = name.value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[\s./]/g, '_')
    .substring(0, 512);
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-content="modalDescription"
      :header-title="modalTitle"
    />
    <form class="flex flex-col w-full gap-4" @submit.prevent="handleTemplate">
      <Input
        v-model="name"
        :label="t('WHATSAPP_TEMPLATES_MGMT.FORM.TITLE.LABEL')"
        :placeholder="t('WHATSAPP_TEMPLATES_MGMT.FORM.TITLE.PLACEHOLDER')"
        :disabled="mode === 'edit'"
        @input="handleNameChange"
      />

      <div class="flex flex-col gap-1">
        <label
          class="mb-0.5 text-sm font-medium text-n-slate-12"
          for="language"
        >
          {{ t('WHATSAPP_TEMPLATES_MGMT.FORM.LANGUAGE.LABEL') }}
        </label>
        <ComboBox
          id="language"
          v-model="language"
          :options="languageOptions"
          :placeholder="t('WHATSAPP_TEMPLATES_MGMT.FORM.LANGUAGE.PLACEHOLDER')"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
          :disabled="mode === 'edit'"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label
          class="mb-0.5 text-sm font-medium text-n-slate-12"
          for="category"
        >
          {{ t('WHATSAPP_TEMPLATES_MGMT.FORM.CATEGORY.LABEL') }}
        </label>
        <ComboBox
          id="category"
          v-model="category"
          :options="categories"
          :placeholder="t('WHATSAPP_TEMPLATES_MGMT.FORM.CATEGORY.PLACEHOLDER')"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
          :disabled="mode === 'edit'"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label
          class="mb-0.5 text-sm font-medium text-n-slate-12"
          for="headerType"
        >
          {{ t('WHATSAPP_TEMPLATES_MGMT.FORM.COMPONENTS.HEADER.LABEL') }}
        </label>
        <ComboBox
          id="headerType"
          v-model="headerType"
          :options="headerTypes"
          :placeholder="t('WHATSAPP_TEMPLATES_MGMT.FORM.CATEGORY.PLACEHOLDER')"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
          @update:model-value="resetMediaFile"
        />
        <div v-if="headerType === 'MEDIA'" class="flex gap-1">
          <ComboBox
            id="headerType"
            v-model="mediaHeaderType"
            :options="mediaHeaderTypes"
            :placeholder="
              t('WHATSAPP_TEMPLATES_MGMT.FORM.CATEGORY.PLACEHOLDER')
            "
            class="[&>div>button]:h-8 [&>div>button]:!outline-n-weak [&>div>button]:hover:!outline-n-strong [&>div>button]:!bg-n-alpha-black2 flex-1"
          />
          <div v-if="mediaHeaderType" class="flex flex-row gap-1 flex-1">
            <FileUpload
              ref="uploadRef"
              v-tooltip.top-end="t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
              :accept="
                mediaHeaderType !== 'IMAGE'
                  ? mediaHeaderType !== 'VIDEO'
                    ? 'application/pdf'
                    : 'video/mp4,video/x-m4v'
                  : 'image/png,image/jpeg,image/jpg'
              "
              :data="{
                direct_upload_url: '/rails/active_storage/direct_uploads',
                direct_upload: true,
              }"
              drop
              :drop-directory="false"
              :multiple="false"
              :size="4096 * 4096"
              input-id="newHeaderMedia"
              @input-file="onIndirectFileUpload"
            >
              <woot-button
                :title="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
                class-names="button--upload"
                color-scheme="secondary"
                emoji="📎"
                icon="attach"
                size="small"
                variant="smooth"
              />
            </FileUpload>
            <div v-if="attachedFiles.length > 0" class="attachment-preview-box">
              <AttachmentPreview
                :attachments="attachedFiles"
                @remove-attachment="resetAttachment"
              />
            </div>
          </div>
        </div>
      </div>

      <TextArea
        v-model="message"
        :label="t('WHATSAPP_TEMPLATES_MGMT.FORM.COMPONENTS.BODY.LABEL')"
        :placeholder="`${t('WHATSAPP_TEMPLATES_MGMT.FORM.COMPONENTS.PLACEHOLDER')}${languageLabel}`"
        :max-length="1024"
        auto-height
        show-character-count
      />

      <div v-if="variables()" class="template__variables-container">
        <p class="variables-label">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams"
          :key="key"
          class="template__variable-item"
        >
          <span class="variable-label">
            {{ key }}
          </span>
          <woot-input
            v-model="processedParams[key]"
            :styles="{ marginBottom: 0 }"
            class="variable-input"
            type="text"
          />
        </div>
      </div>

      <Input
        v-model="footerText"
        :label="t('WHATSAPP_TEMPLATES_MGMT.FORM.COMPONENTS.FOOTER.LABEL')"
        :placeholder="`${t('WHATSAPP_TEMPLATES_MGMT.FORM.COMPONENTS.PLACEHOLDER')}${languageLabel}`"
      />

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          type="submit"
          :label="submitButtonText"
          :disabled="isSubmitDisabled"
          :is-loading="addTemplate.showLoading"
        />
        <button class="button clear" @click.prevent="emit('close')">
          {{ t('WHATSAPP_TEMPLATES_MGMT.FORM.CANCEL_BUTTON_TEXT') }}
        </button>
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
.variables-label {
  @apply text-sm font-semibold mb-2.5;
}

.template__variable-item {
  @apply items-center flex mb-2.5;

  .label {
    @apply text-xs;
  }

  .variable-input {
    @apply flex-1 text-sm ml-2.5;
  }

  .variable-label {
    @apply bg-slate-75 dark:bg-slate-700 text-slate-700 dark:text-slate-100 inline-block rounded-md text-xs py-2.5 px-6;
  }
}
</style>
